import React from 'react';
import { Card, EmptyState, StatusBadge } from '@/components/ds';
import { Building2, Edit2, Archive, Trash2, Plus, Briefcase, ShieldOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';

/**
 * AgencyCarrierPortfolio — carrier-first unified view of appointments
 * AND enablement caps. Mirrors the agent profile's `AgentCarrierPortfolio`
 * layout: one card per carrier, everything the operator needs to
 * understand this carrier relationship in a single glance.
 *
 * Each carrier card can render:
 *   • The enablement row (level cap + enable/disable state)  [KR only]
 *   • Every appointment (writing #, level, contract type, status, dates)
 *
 * If a carrier is enabled but has no appointment yet, or appointed but
 * not enabled, the card surfaces it via inline badges so KR sees the
 * gap instead of having to cross-reference two separate lists.
 */
const buildCarrierGroups = (appointments = [], enabledCarriers = []) => {
  const map = new Map();
  const upsert = (carrierId, carrierName) => {
    const key = carrierId || carrierName || 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        key,
        carrier_id: carrierId,
        carrier_name: carrierName || 'Unknown',
        enablements: [],
        appointments: [],
      });
    }
    return map.get(key);
  };

  enabledCarriers.forEach((e) => {
    upsert(e.carrier_id, e.carrier_name).enablements.push(e);
  });
  appointments.forEach((a) => {
    upsert(a.carrier_id, a.carrier_name).appointments.push(a);
  });

  // Sort inside each group: Active/Approved first, then pending, then terminals.
  const rank = (a) => {
    const s = a.appointment_status;
    if (s === 'Active') return 0;
    if (s === 'Approved') return 1;
    if (s === 'Pending') return 2;
    return 3;
  };
  return Array.from(map.values())
    .map((g) => ({ ...g, appointments: g.appointments.slice().sort((a, b) => rank(a) - rank(b)) }))
    .sort((a, b) => a.carrier_name.localeCompare(b.carrier_name));
};

const apptStatusVariant = (status) => {
  if (status === 'Active') return 'success';
  if (status === 'Approved') return 'success';
  if (status === 'Pending') return 'warning';
  if (status === 'Terminated' || status === 'Inactive') return 'danger';
  return 'neutral';
};

export const AgencyCarrierPortfolio = ({
  appointments = [],
  enabledCarriers = [],
  isKR = false,
  onAddAppointment,
  onEditAppointment,
  onDeleteAppointment,
  onEnableCarrier,
  onDisableCarrier,
}) => {
  const groups = buildCarrierGroups(appointments, enabledCarriers);
  const activeCount = appointments.filter((a) => a.appointment_status === 'Active').length;

  return (
    <Card
      title="Carrier Portfolio"
      description={`${activeCount} active · ${appointments.length} appointment${appointments.length === 1 ? '' : 's'} · ${enabledCarriers.length} enabled across ${groups.length} carrier${groups.length === 1 ? '' : 's'}`}
      icon={Briefcase}
      headerActions={isKR ? (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onEnableCarrier?.()} data-testid="add-carrier-btn">
            <Plus size={13} className="mr-1" /> Enable Carrier
          </Button>
          <Button size="sm" onClick={() => onAddAppointment?.()} data-testid="add-agency-appt-btn">
            <Plus size={13} className="mr-1" /> Add Appointment
          </Button>
        </div>
      ) : null}
      testId="agency-carrier-portfolio"
    >
      {groups.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No carrier relationships yet"
          description={isKR
            ? 'Enable a carrier or add an appointment to start building the agency book.'
            : 'This agency has no active carrier appointments or enablements.'}
          testId="no-agency-carriers"
          actions={isKR ? (
            <Button size="sm" onClick={() => onAddAppointment?.()}>
              <Plus size={14} className="mr-1" /> Add Appointment
            </Button>
          ) : null}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {groups.map((g) => {
            const hasEnablement = g.enablements.length > 0;
            const hasAppointment = g.appointments.length > 0;
            const totalRows = g.appointments.length + (hasEnablement ? 1 : 0);
            return (
              <div
                key={g.key}
                className="rounded-xl border p-4 hover:border-[color:var(--kr-gold-500)] transition-colors"
                style={{ borderColor: 'var(--kr-border)' }}
                data-testid={`agency-portfolio-carrier-${g.carrier_id || g.carrier_name}`}
              >
                {/* Carrier header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--kr-navy-50)', color: 'var(--kr-navy-700)' }}
                    >
                      <Building2 size={17} />
                    </div>
                    <h4 className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--kr-navy-900)' }}>
                      {g.carrier_name}
                    </h4>
                  </div>
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--kr-subtle)' }}
                  >
                    {totalRows} row{totalRows === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Enablement + appointments intermixed */}
                <div className="space-y-2">
                  {/* ── Enablement row(s) ─ shown ONLY when KR is looking ── */}
                  {isKR && g.enablements.map((e) => (
                    <div
                      key={e.enablement_id}
                      className="p-2.5 rounded-lg border-l-4 border-l-brand-500 border border-slate-200 bg-brand-50/40 flex items-start justify-between gap-2 text-xs"
                      data-testid={`enabled-carrier-${e.enablement_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="text-[9px] bg-brand-100 text-brand-800 border-brand-200">
                            Enablement
                          </Badge>
                          <StatusBadge variant={e.enabled ? 'success' : 'neutral'}>
                            {e.enabled ? 'Enabled' : 'Disabled'}
                          </StatusBadge>
                          {e.level_cap ? (
                            <Badge variant="outline" className="text-[10px]">
                              Max Level: {e.level_cap.max_level_name}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                              Street Level Only
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5" style={{ color: 'var(--kr-muted)' }}>
                          {e.contract_type_name && <span>Type: <span className="font-medium" style={{ color: 'var(--kr-navy-800)' }}>{e.contract_type_name}</span></span>}
                          {e.level_cap?.effective_date && (
                            <span className="col-span-2">Effective {formatDate(e.level_cap.effective_date)}</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onDisableCarrier?.(e)}
                        className="p-1 rounded hover:bg-red-50 transition-colors flex-shrink-0"
                        title="Disable / remove enablement"
                        data-testid={`disable-carrier-${e.enablement_id}`}
                      >
                        <ShieldOff size={13} className="text-red-500" />
                      </button>
                    </div>
                  ))}

                  {/* ── Appointment row(s) ── */}
                  {g.appointments.map((row) => (
                    <div
                      key={row.appointment_id}
                      className="p-2.5 rounded-lg border flex items-start justify-between gap-2 text-xs"
                      style={{ borderColor: 'var(--kr-border)', background: '#fafbfc' }}
                      data-testid={`agency-portfolio-appt-${row.appointment_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusBadge variant={apptStatusVariant(row.appointment_status)}>
                            {row.appointment_status || '—'}
                          </StatusBadge>
                          {row.contract_type_name && (
                            <span className="font-medium" style={{ color: 'var(--kr-navy-800)' }}>{row.contract_type_name}</span>
                          )}
                        </div>
                        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5" style={{ color: 'var(--kr-muted)' }}>
                          {(row.level_name || row.level_code) && (
                            <span>Level: <span className="font-medium" style={{ color: 'var(--kr-navy-800)' }}>{row.level_name || row.level_code}</span></span>
                          )}
                          {row.writing_number && <span className="font-mono truncate">#{row.writing_number}</span>}
                          {row.effective_date && <span className="col-span-2">Effective {formatDate(row.effective_date)}</span>}
                        </div>
                      </div>
                      {isKR && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => onEditAppointment?.(row)}
                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                            title="Edit"
                            data-testid={`edit-agency-appt-${row.appointment_id}`}
                          >
                            <Edit2 size={13} style={{ color: 'var(--kr-muted)' }} />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteAppointment?.(row)}
                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                            title={row.appointment_status === 'Active' && row.writing_number ? 'Archive' : 'Delete'}
                            data-testid={`delete-agency-appt-${row.appointment_id}`}
                          >
                            {row.appointment_status === 'Active' && row.writing_number
                              ? <Archive size={13} className="text-amber-500" />
                              : <Trash2 size={13} className="text-red-500" />}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* ── Cross-check hint: enabled but no appointment yet ── */}
                  {isKR && hasEnablement && !hasAppointment && (
                    <div className="text-[11px] italic text-amber-700 px-1" data-testid={`missing-appt-${g.key}`}>
                      Enabled but no appointment yet — add one to activate downline access.
                    </div>
                  )}
                  {isKR && !hasEnablement && hasAppointment && (
                    <div className="text-[11px] italic text-amber-700 px-1" data-testid={`missing-enable-${g.key}`}>
                      Appointment exists but this carrier is not enabled for downline requests.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default AgencyCarrierPortfolio;
